import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db, doc, getDoc } from '../services/firebase';
import Header from '../components/Header';
import Footer from '../components/Footer';
import html2pdf from 'html2pdf.js';

// A4 aspect ratio is 1:√2 (≈ 1:1.4142)
const A4_ASPECT_RATIO = 1.4142;
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ReceiptPage = () => {
  const { orderId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const query = useQuery();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolvedOrderId, setResolvedOrderId] = useState(orderId);
  const receiptRef = useRef(null);

  // Generate PDF Receipt
  const generatePDFReceipt = () => {
    if (!order || !receiptRef.current) return;

    // Add CSS for page breaks
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        .page-break-before { page-break-before: always; }
        .page-break-after { page-break-after: always; }
        .page-break-inside-avoid { page-break-inside: avoid; }
        table { page-break-inside: auto; }
        tr { page-break-inside: avoid; page-break-after: auto; }
        td { page-break-inside: avoid; page-break-after: auto; }
        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }
      }
    `;
    document.head.appendChild(style);

    // Temporarily remove height constraint to measure full content
    const originalHeight = receiptRef.current.style.height;
    receiptRef.current.style.height = 'auto';
    receiptRef.current.style.minHeight = 'auto';

    const opt = {
      margin: 0,
      filename: `GREY-Invoice-${String(resolvedOrderId).slice(-4).padStart(4, '0')}.pdf`,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true,
        // Let html2canvas automatically handle the full content height
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait',
        putOnlyUsedFonts: true,
        floatPrecision: 16
      },
      // Enable automatic page breaks
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    html2pdf().set(opt).from(receiptRef.current).save()
      .then(() => {
        // Restore original height
        receiptRef.current.style.height = originalHeight;
        // Remove the temporary style
        document.head.removeChild(style);
      })
      .catch((error) => {
        console.error('PDF generation failed:', error);
        // Restore original height even if PDF generation fails
        receiptRef.current.style.height = originalHeight;
        // Remove the temporary style
        if (document.head.contains(style)) {
          document.head.removeChild(style);
        }
      });
  };

  useEffect(() => {
    // If orderId is missing or 'undefined', try to resolve from SkipCash return parameters
    if (!orderId || orderId === 'undefined') {
      const transId = query.get('transId');
      if (transId && transId.startsWith('ORDER-')) {
        // Extract the order ID from the transaction ID
        const extractedOrderId = transId.replace('ORDER-', '');
        setResolvedOrderId(extractedOrderId);
          } else {
        setError('Order ID not found in URL parameters.');
            setLoading(false);
          }
    } else {
      setResolvedOrderId(orderId);
    }
  }, [orderId, query]);

  useEffect(() => {
    const loadOrderData = async () => {
      setLoading(true);
      try {
        // Check for cash on delivery order data first
        const cashOrderData = localStorage.getItem('orderData');
        if (cashOrderData) {
          const parsedData = JSON.parse(cashOrderData);
          setOrder(parsedData);
          localStorage.removeItem('orderData');
          setLoading(false);
          return;
        }

        // Load order from Firestore
        const orderDoc = await getDoc(doc(db, 'orders', resolvedOrderId));
        if (orderDoc.exists()) {
          const orderData = { id: orderDoc.id, ...orderDoc.data() };
          
          // Handle webhook-created orders with minimal data
          if (orderData.webhookFirst && (!orderData.items || orderData.items.length === 0)) {
            // Try to find cart data from localStorage backup
            const backupData = localStorage.getItem('checkoutBackup');
            if (backupData) {
              try {
                const backup = JSON.parse(backupData);
                if (backup.cartItems && backup.cartItems.length > 0) {
                  orderData.items = backup.cartItems;
                  orderData.shipping = backup.formData;
                  orderData.userEmail = backup.formData?.email;
                  orderData.userName = `${backup.formData?.firstName || ''} ${backup.formData?.lastName || ''}`.trim();
                  orderData.total = backup.totalPrice;
                  console.log('Enhanced webhook order with backup data');
                }
              } catch (e) {
                console.error('Failed to parse backup data:', e);
              }
            }
            
            // If still no items, create a placeholder
            if (!orderData.items || orderData.items.length === 0) {
              orderData.items = [{
                id: 'payment-recovery',
                name: 'Payment Processed - Items Being Recovered',
                price: orderData.amount || 0,
                quantity: 1,
                image: '/images/logo.png',
                note: 'Order details will be updated shortly'
              }];
            }
          }
          
          setOrder(orderData);
        } else {
          setError('Order not found');
        }
      } catch (err) {
        console.error('Error loading order:', err);
        setError('Failed to load order details');
      }
      setLoading(false);
    };

    if (resolvedOrderId) {
      loadOrderData();
    }
  }, [resolvedOrderId]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!order) {
    return <div className="error">Order not found</div>;
  }

  // Format date to match invoice style
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date.seconds * 1000);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  };

  // Generate INV number (4 digits)
  const invNumber = String(resolvedOrderId).slice(-4).padStart(4, '0');

  return (
    <>
      <Header />
      <div style={{
        maxWidth: '1000px',
        margin: '40px auto',
        padding: '20px'
      }}>
        {/* Download Button */}
        <div style={{ 
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={generatePDFReceipt}
            style={{
              background: '#222',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 11V14H2V11H0V14C0 15.1 0.9 16 2 16H14C15.1 16 16 15.1 16 14V11H14Z" fill="currentColor"/>
              <path d="M7 12L7.7 11.3L9 10V6H7V10L5.3 8.3L4.6 9L7 11.4L7 12Z" fill="currentColor"/>
              <path d="M14 4H11V0H5V4H2L8 10L14 4ZM7 2H9V4H7V2Z" fill="currentColor"/>
            </svg>
            Download PDF
          </button>
        </div>

        {/* A4 Container */}
        <div 
          ref={receiptRef}
          style={{
            width: '210mm',
            minHeight: '297mm',
            margin: '0 auto',
            background: '#fff',
            boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
            position: 'relative',
            padding: '20mm',
            boxSizing: 'border-box',
            fontFamily: 'Inter, system-ui, sans-serif',
            color: '#000',
            // CSS for proper page breaks
            pageBreakInside: 'auto',
            WebkitPrintColorAdjust: 'exact',
            colorAdjust: 'exact'
          }}
        >
          {/* Header */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            marginBottom: '40px',
            position: 'relative'
          }}>
            {/* Left Column */}
            <div style={{ flex: 1 }}>
              <img 
                src="/images/logo.png" 
                alt="GREY SPECIALTY COFFEE & ROASTERY" 
                style={{ 
                  width: '45mm',
                  marginBottom: '15mm',
                  display: 'block'
                }}
              />
              <div style={{ 
                fontSize: '11pt',
                color: '#333',
                marginBottom: '4mm'
              }}>
                Grey Speciality Coffee & Roastery
              </div>
              <div style={{ 
                fontSize: '10pt',
                color: '#666',
                lineHeight: '5mm'
              }}>
                <div>Doha , Qatar</div>
                <div>Alwakra</div>
                <div>50008852</div>
                <div>greyroastery@gmail.com</div>
              </div>
            </div>

            {/* Right Column */}
            <div style={{ textAlign: 'right', minWidth: '120px' }}>
              <div style={{ 
                fontSize: '11pt',
                marginBottom: '4mm'
              }}>
                INVOICE
              </div>
              <div style={{ 
                color: '#333',
                fontSize: '10pt',
                marginBottom: '8mm'
              }}>
                INV{invNumber}
              </div>
              <div style={{ marginBottom: '8mm' }}>
                <div style={{ color: '#666', fontSize: '10pt' }}>DATE</div>
                <div style={{ fontSize: '10pt' }}>{formatDate(order.createdAt)}</div>
              </div>
              <div>
                <div style={{ color: '#666', fontSize: '10pt' }}>BALANCE DUE</div>
                <div style={{ fontSize: '10pt' }}>QAR {order.total?.toLocaleString()} ريال</div>
              </div>
            </div>
          </div>

          {/* Divider Line */}
          <div style={{
            width: '100%',
            height: '1px',
            background: '#eee',
            margin: '16px 0'
          }} />

          {/* Bill To Section */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ 
              fontSize: '10pt',
              marginBottom: '4mm'
            }}>
              BILL TO
            </div>
            <div style={{ 
              fontSize: '11pt',
              color: '#333'
            }}>
              {order.shipping ? `${order.shipping.firstName} ${order.shipping.lastName}` : 'Customer'}
            </div>
          </div>

          {/* Items Table */}
          <div style={{ 
            marginBottom: '24px',
            pageBreakInside: 'avoid'
          }}>
            <table style={{ 
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '10pt',
              pageBreakInside: 'auto'
            }}>
              <thead>
                <tr>
                  <th style={{ 
                    textAlign: 'left',
                    padding: '3mm 2mm',
                    color: '#333',
                    fontWeight: '500',
                    width: '50%',
                    borderBottom: '1px solid #eee'
                  }}>DESCRIPTION</th>
                  <th style={{ 
                    textAlign: 'right',
                    padding: '3mm 2mm',
                    color: '#333',
                    fontWeight: '500',
                    width: '20%',
                    borderBottom: '1px solid #eee'
                  }}>RATE</th>
                  <th style={{ 
                    textAlign: 'right',
                    padding: '3mm 2mm',
                    color: '#333',
                    fontWeight: '500',
                    width: '10%',
                    borderBottom: '1px solid #eee'
                  }}>QTY</th>
                  <th style={{ 
                    textAlign: 'right',
                    padding: '3mm 2mm',
                    color: '#333',
                    fontWeight: '500',
                    width: '20%',
                    borderBottom: '1px solid #eee'
                  }}>AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                {order.items?.map((item, index) => {
                  // Debug log to see item structure
                  console.log(`Receipt Item ${index}:`, {
                    name: item.name,
                    selectedWeight: item.selectedWeight,
                    selectedBeanType: item.selectedBeanType,
                    weight: item.weight,
                    beanType: item.beanType,
                    selectedOptions: item.selectedOptions,
                    fullItem: item
                  });
                  
                  return (
                  <tr key={index} style={{ 
                    borderBottom: '1px solid #eee',
                    pageBreakInside: 'avoid',
                    pageBreakAfter: 'auto'
                  }}>
                    <td style={{ padding: '3mm 2mm', verticalAlign: 'top' }}>
                      <div style={{ fontWeight: '500' }}>{item.name}</div>
                      {/* Weight and Bean Type Details */}
                      <div style={{ color: '#666', fontSize: '9pt', marginTop: '1mm', lineHeight: '1.3' }}>
                        {/* Display weight - handles both "250g" format and raw numbers */}
                        {item.selectedWeight?.label && (
                          <div>Weight: {item.selectedWeight.label}</div>
                        )}
                        {!item.selectedWeight?.label && item.weight && (
                          <div>Weight: {item.weight}g</div>
                        )}
                        
                        {/* Display bean type */}
                        {item.selectedBeanType && (
                          <div>Type: {item.selectedBeanType.charAt(0).toUpperCase() + item.selectedBeanType.slice(1)}</div>
                        )}
                        {!item.selectedBeanType && item.beanType && (
                          <div>Type: {item.beanType.charAt(0).toUpperCase() + item.beanType.slice(1)}</div>
                        )}
                        
                        {/* Additional product options from selectedOptions */}
                        {item.selectedOptions && Object.entries(item.selectedOptions).map(([key, value]) => {
                          if (key.toLowerCase().includes('weight') || key.toLowerCase().includes('type')) {
                            return null; // Skip if already handled above
                          }
                          return (
                            <div key={key}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}: {value}</div>
                          );
                        })}
                        
                        {/* Debug: Show if no weight/type info found */}
                        {!item.selectedWeight?.label && !item.weight && !item.selectedBeanType && !item.beanType && (
                          <div style={{ color: '#ccc', fontStyle: 'italic' }}>Product specifications not available</div>
                        )}
                      </div>
                      {item.note && (
                        <div style={{ color: '#888', fontSize: '9pt', marginTop: '1mm', fontStyle: 'italic' }}>
                          Note: {item.note}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '3mm 2mm', textAlign: 'right' }}>QAR {item.price} ريال</td>
                    <td style={{ padding: '3mm 2mm', textAlign: 'right' }}>{item.quantity}</td>
                    <td style={{ padding: '3mm 2mm', textAlign: 'right' }}>
                      QAR {(item.price * item.quantity).toLocaleString()} ريال
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div style={{ 
            marginLeft: 'auto',
            width: '60mm',
            fontSize: '10pt',
            textAlign: 'right',
            pageBreakInside: 'avoid'
          }}>
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
              padding: '2mm 0'
            }}>
              <span>SUBTOTAL</span>
              <span>QAR {(order.subtotal || (order.total ? order.total - (order.deliveryFee || 30) : 0)).toLocaleString()} ريال</span>
            </div>
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
              padding: '2mm 0'
            }}>
              <span>DELIVERY FEE</span>
              <span>{order.deliveryFee === 0 ? 'Free' : `QAR ${(order.deliveryFee || 30).toLocaleString()} ريال`}</span>
            </div>
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '12px',
              padding: '2mm 0',
              borderTop: '1px solid #eee',
              fontWeight: 'bold'
            }}>
              <span>TOTAL</span>
              <span>QAR {order.total?.toLocaleString()} ريال</span>
            </div>
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <span>BALANCE DUE</span>
              <span>QAR {order.total?.toLocaleString()} ريال</span>
            </div>
            {order.status === 'paid' && (
              <div style={{
                background: 'rgb(232, 245, 233)',
                color: 'rgb(46, 125, 50)',
                padding: '2mm 4mm',
                borderRadius: '2mm',
                display: 'inline-block',
                fontSize: '9pt'
              }}>
                PAID
              </div>
            )}
          </div>

          {/* Shipping Information */}
          {order.shipping && (
            <div style={{ 
              marginTop: '32px',
              fontSize: '10pt'
            }}>
              <div style={{ 
                fontSize: '11pt',
                color: '#333',
                marginBottom: '6mm'
              }}>
                Shipping Information
              </div>
              <div style={{ 
                color: '#666',
                lineHeight: '6mm'
              }}>
                <div>{order.shipping.firstName} {order.shipping.lastName}</div>
                <div>{order.shipping.street}</div>
                <div>{order.shipping.city}, {order.shipping.state} {order.shipping.postalCode}</div>
                <div>Phone: {order.shipping.phone}</div>
                <div>Email: {order.shipping.email}</div>
              </div>
            </div>
          )}

          {/* Payment Status */}
          <div style={{ 
            marginTop: '32px',
            fontSize: '10pt',
            pageBreakInside: 'avoid'
          }}>
            <div style={{ 
              fontSize: '11pt',
              color: '#333',
              marginBottom: '6mm'
            }}>
              Payment Information
            </div>
            <div style={{ 
              color: '#666',
              lineHeight: '6mm'
            }}>
              <div>Payment Method: {order.paymentMethod === 'cash' ? 'Cash on Delivery' : 'Online Payment'}</div>
              <div style={{ fontWeight: 700 }}>
                Payment Status: {order.status === 'paid' ? (
                  <span style={{ color: '#2ecc71' }}>Paid</span>
                ) : (
                  <span style={{ color: '#e74c3c' }}>Unpaid (Cash on Delivery)</span>
                )}
              </div>
              {order.incompleteOrder && (
                <div style={{ 
                  marginTop: '6mm', 
                  padding: '3mm', 
                  background: '#fff3cd', 
                  border: '1px solid #ffeaa7',
                  borderRadius: '2mm',
                  fontSize: '9pt',
                  color: '#856404'
                }}>
                  ⚠️ This order was recovered from payment confirmation. Some details may be incomplete.
                </div>
              )}
            </div>
          </div>
        </div>
    </div>
      <Footer />
    </>
  );
};

export default ReceiptPage; 